import React, { useState } from "react";
import { Outlet, NavLink, useNavigate, Link } from "react-router-dom";
import {
  LayoutDashboard, ListChecks, AlertTriangle, FlaskConical,
  FileBarChart2, Upload as UploadIcon, FormInput, ScrollText,
  Leaf, Sparkles, LogOut, Building2, BarChart3, Paperclip, Settings,
  Globe2, HeartPulse, FilePlus2,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { cn } from "../lib/utils";
import AdviserDrawer from "./AdviserDrawer";
import AppFooter from "./AppFooter";

const NAV = [
  { to: "/", label: "Executive Dashboard", icon: LayoutDashboard },
  { to: "/scorecard", label: "KPI Scorecard", icon: ListChecks },
  { to: "/benchmarks", label: "Industry Benchmarks", icon: BarChart3 },
  { to: "/anomalies", label: "Anomaly Workbench", icon: AlertTriangle },
  { to: "/initiatives", label: "Initiatives", icon: FlaskConical },
  { to: "/data-entry", label: "Data Entry", icon: FormInput },
  { to: "/upload", label: "Bulk Upload (CSV)", icon: UploadIcon },
  { to: "/evidence", label: "Evidence Documents", icon: Paperclip },
  { to: "/reports", label: "Reports", icon: FileBarChart2 },
  { to: "/audit", label: "Audit Trail", icon: ScrollText },
];

const PLATFORM_NAV = [
  { to: "/platform/ibpap", label: "IBPAP Consolidated", icon: Globe2 },
  { to: "/platform/health", label: "Tenants Health", icon: HeartPulse },
  { to: "/platform/new-tenant", label: "Provision Tenant", icon: FilePlus2 },
];

const ADMIN_ROLES = new Set(["platform_admin", "tenant_admin"]);

export default function Layout() {
  const { user, logout } = useAuth();
  const [adviserOpen, setAdviserOpen] = useState(false);
  const nav = useNavigate();

  const tenantName = user?.tenant?.name || (user?.role === "platform_admin" ? "All tenants" : "");

  return (
    <div className="min-h-screen flex bg-white">
      {/* Sidebar */}
      <aside className="w-[240px] shrink-0 border-r border-border flex flex-col">
        <div className="px-4 py-4 border-b border-border flex items-center gap-2">
          <div className="h-8 w-8 rounded-md bg-brand text-white flex items-center justify-center">
            <Leaf size={16} />
          </div>
          <div className="leading-tight">
            <div className="text-sm font-semibold">ITBPM ESG</div>
            <div className="text-[11px] text-ink-soft">Intelligence Platform</div>
          </div>
        </div>

        <nav className="px-2 py-3 flex-1 overflow-y-auto">
          <div className="section-label px-3 mb-2">Navigation</div>
          {NAV.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to} to={n.to} end={n.to === "/"}
                data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={({isActive}) => cn("nav-link", isActive && "active")}
              >
                <Icon size={16} /> <span>{n.label}</span>
              </NavLink>
            );
          })}
          {ADMIN_ROLES.has(user?.role) && (
            <>
              <div className="section-label px-3 mt-4 mb-1">Administration</div>
              <NavLink
                to="/admin"
                data-testid="nav-admin"
                className={({isActive}) => cn("nav-link", isActive && "active")}
              >
                <Settings size={16}/> <span>Admin Module</span>
              </NavLink>
            </>
          )}
          {user?.role === "platform_admin" && (
            <>
              <div className="section-label px-3 mt-4 mb-1">Platform Admin</div>
              {PLATFORM_NAV.map((n) => {
                const Icon = n.icon;
                return (
                  <NavLink
                    key={n.to} to={n.to}
                    data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className={({isActive}) => cn("nav-link", isActive && "active")}
                  >
                    <Icon size={16} /> <span>{n.label}</span>
                  </NavLink>
                );
              })}
            </>
          )}
        </nav>

        <div className="px-3 py-3 border-t border-border">
          <button
            data-testid="open-adviser-button"
            onClick={() => setAdviserOpen(true)}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-md bg-brand-50 text-brand-900 hover:bg-brand-100 transition border border-brand-100"
          >
            <Sparkles size={14} className="text-brand" /> <span className="text-xs font-semibold">Ask ESG Adviser</span>
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Topbar */}
        <header className="h-14 px-6 border-b border-border flex items-center justify-between bg-white">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-sm">
              <Building2 size={14} className="text-ink-soft" />
              <span className="text-ink-soft">Tenant</span>
              <span data-testid="topbar-tenant" className="font-semibold text-ink">{tenantName}</span>
            </div>
            {user?.tenant?.current_period && (
              <span className="pill pill-info" data-testid="topbar-period">Period · {user.tenant.current_period}</span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right leading-tight">
              <div className="text-sm font-medium" data-testid="topbar-user-name">{user?.name}</div>
              <div className="text-[11px] text-ink-soft uppercase tracking-widest">{user?.role?.replaceAll("_", " ")}</div>
            </div>
            <button
              data-testid="logout-button"
              onClick={logout}
              className="btn btn-secondary"
              title="Sign out"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </header>

        {/* Outlet */}
        <main className="flex-1 overflow-y-auto bg-white">
          <Outlet />
        </main>
        <AppFooter />
      </div>

      {/* Adviser drawer */}
      <AdviserDrawer open={adviserOpen} onClose={() => setAdviserOpen(false)} />
    </div>
  );
}
